/**
 * ReportArtistModal — Discrete compliance report modal.
 * Collects reason, email, evidence and submits to POST /api/public/reports.
 */
import { useState, useEffect } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { submitReport } from '../services/api';

interface ReportArtistModalProps {
  tenantId: number;
  artistName: string;
  onClose: () => void;
  /** The user's email, auto-filled if logged in */
  userEmail?: string;
}

const REASONS = [
  { value: 'Copyright Infringement', label: 'Copyright Infringement', description: 'This artist is uploading music they do not own.' },
  { value: 'Impersonation', label: 'Impersonation', description: 'This artist is pretending to be someone else.' },
  { value: 'AI Policy Violation', label: 'AI Policy Violation', description: 'Unlabeled fully-synthetic or impersonation vocals.' },
  { value: 'Offensive Content', label: 'Offensive Content', description: 'Hate speech or prohibited material.' },
  { value: 'Other', label: 'Other', description: 'Requires text explanation.' },
];

export default function ReportArtistModal({ tenantId, artistName, onClose, userEmail }: ReportArtistModalProps) {
  const [email, setEmail] = useState(userEmail || '');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reportId, setReportId] = useState<number | null>(null);

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleSubmit = async () => {
    setError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!reason) {
      setError('Please select a reason.');
      return;
    }
    if (reason === 'Other' && evidence.trim().length < 10) {
      setError("When selecting 'Other', please provide a detailed explanation (at least 10 characters).");
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitReport({
        tenantId,
        email: email.trim().toLowerCase(),
        reason,
        evidence: evidence.trim() || undefined,
        pageUrl,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null) as { error?: string } | null;
        setError(err?.error || `Submission failed (${res.status})`);
        return;
      }
      const json = await res.json() as { data: { id: number; message: string } };
      setReportId(json.data.id);
    } catch (err: any) {
      setError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Success view
  if (reportId !== null) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative w-full max-w-md glass-card p-8 text-center" onClick={(e) => e.stopPropagation()}>
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">Report Logged</h3>
          <p className="text-sm text-text-secondary mb-1">Your report has been submitted successfully.</p>
          <p className="font-mono text-electric-cyan text-sm mb-6">ID#{reportId}</p>
          <p className="text-xs text-text-muted mb-6">Our compliance team will review this report. Thank you for helping keep fans.audio safe.</p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-lg bg-electric-cyan text-deep-charcoal font-semibold text-sm hover:bg-electric-cyan/90 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg glass-card p-6 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded hover:bg-white/10 transition-colors">
          <X className="w-5 h-5 text-text-secondary" />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          <h2 className="text-lg font-bold">Report {artistName}</h2>
        </div>
        <p className="text-xs text-text-secondary mb-5">Submit a compliance or copyright complaint about this artist.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-4 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Email */}
        <label className="block mb-4">
          <span className="text-xs font-mono text-text-muted uppercase tracking-wider">Your Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none transition-colors"
          />
        </label>

        {/* Reason */}
        <div className="mb-4">
          <span className="text-xs font-mono text-text-muted uppercase tracking-wider block mb-2">Reason</span>
          <div className="space-y-2">
            {REASONS.map((r) => (
              <label
                key={r.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  reason === r.value
                    ? 'bg-electric-cyan/5 border-electric-cyan/30'
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
              >
                <input
                  type="radio"
                  name="reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="mt-0.5 accent-[#00F5FF]"
                />
                <div>
                  <p className="text-sm font-medium text-white">{r.label}</p>
                  <p className="text-xs text-text-muted">{r.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Evidence */}
        <label className="block mb-5">
          <span className="text-xs font-mono text-text-muted uppercase tracking-wider">
            Evidence {reason === 'Other' ? '(required)' : '(optional)'}
          </span>
          <textarea
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="Please provide links or details to support this report."
            rows={4}
            className="mt-1 w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none transition-colors resize-none"
          />
        </label>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full px-4 py-2.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 font-semibold text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting…
            </>
          ) : (
            'Submit Report'
          )}
        </button>
      </div>
    </div>
  );
}
